import {
  Box,
  Grid,
  IconButton,
  Paper,
  Typography,
  makeStyles
} from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import { scrollPageToTop } from "Routes/Routes";
import instance from "api/globalaxios";
import CircularCenteredLoader from "features/common/CircularCenteredLoader";
import Tabs from "features/Tabs/Tabs";
import DownloadSvg from "images/download.svg";
import searchSvg from "images/search.svg";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { formatDate } from "utils";
import SebiInvoiceTable from "Admin/Sebi/Finance/SebiInvoiceTable";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(2),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  heading: {
    fontSize: "24px",
    lineHeight: "28px",
    letterSpacing: "0.3px",
    color: "#252733",
    marginLeft: "20px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
    },
  },
  username: {
    fontWeight: "bold",
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0.2px",
    color: "#252733",
  },
  tableHead: {
    color: "#000",
    fontSize: "15px",
    fontWeight: "500",
    wordBreak: "break-word",
  },
  tableData: {
    color: "black",
    lineHeight: "100%",
    fontSize: "13px",
  },
  vendorTabs: {
    gap: "1.5rem",
    width: "auto",
    justifyContent: "flex-start",
    padding: "0 1rem",
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: "0.7rem",
    maxWidth: "480px",
    border: "1px solid #828282",
    boxSizing: "border-box",
    padding: "0.5rem 0.7rem",
    margin: "1.5rem 1rem",
    boxShadow: "none",
  },
  searchInput: {
    border: "none",
    outline: "none",
    width: "100%",
    fontSize: "14px",
    background: "transparent",
  },
  tableRow: {
    padding: "16px",
    borderBottom: "1px solid #E5E5E5",
    textAlign: "left",
    "&:hover": {
      backgroundColor: "#f7f7f7",
    },
  },
}));

export const downloadInvoice = async (id) => {
  try {
    const response = await instance({
      method: "get",
      url: `/surepay/download_invoice/${id}/`,
    });
    window.location.href = response?.data?.url;
  } catch (err) {
    console.log(err);
  }
};

const VENDOR_CATEGORIES = [
  { id: "NSE", label: "NSE" },
  { id: "NSDL", label: "NSDL" },
];

export default function InvoiceList({ profile, showPaymentDetails, isVendor }) {
  const classes = useStyles();
  const [invoiceList, setInvoiceList] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  // Vendor view only: invoices are split by category and searchable.
  const [category, setCategory] = useState("NSE");
  const [searchText, setSearchText] = useState("");
  const [searchValue, setSearchValue] = useState("");
  // Ignores a response that arrives after a newer request was sent, e.g.
  // when switching tabs or typing while a fetch is still in flight.
  const latestRequest = useRef(0);

  // Search 500ms after the user stops typing.
  useEffect(() => {
    const timer = setTimeout(() => setSearchValue(searchText.trim()), 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  const getInvoiceList = useCallback(async (page) => {
    const requestId = ++latestRequest.current;
    let url = `/surepay/invoice_paymentlink_dashboard/?page=${page}`;
    if (isVendor) {
      url += `&category=${category}`;
      if (searchValue) {
        url += `&search_value=${encodeURIComponent(searchValue)}`;
      }
    }
    try {
      setLoadingInvoices(true);
      const response = await instance({
        method: "get",
        url,
      });
      if (requestId !== latestRequest.current) return;
      const count = response?.data?.count;
      const t = parseInt(count / 100);
      const r = count % 100;
      const total = t + (r > 0 ? 1 : 0);
      setTotalPages(total);
      setInvoiceList(response?.data?.results);
      setLoadingInvoices(false);
    } catch (err) {
      console.log(err);
      if (requestId === latestRequest.current) setLoadingInvoices(false);
    }
  }, [isVendor, category, searchValue]);

  // Runs on mount, and again when the vendor switches tab or searches.
  useEffect(() => {
    setPage(1);
    getInvoiceList(1);
  }, [getInvoiceList]);

  const handlePageChange = (event, value) => {
    if (page === value) {
      return;
    }
    scrollPageToTop();
    setPage(value);
    getInvoiceList(value);
  };

  return (
    <div className={classes.root}>
      <Grid container justify="flex-start" direction="column">
        <Grid item>
          <Grid
            container
            justify="space-between"
            direction="row"
            style={{ padding: "10px" }}
          >
            <Grid item>
              <Typography className={classes.heading}>Invoices</Typography>
            </Grid>
            <Grid item>
              <Typography className={classes.username}>
                {profile.user.first_name} {profile.user.last_name}
              </Typography>
            </Grid>
          </Grid>
          {isVendor ? (
            <Box style={{ marginTop: "2rem" }}>
              <Tabs
                tabs={VENDOR_CATEGORIES}
                handleClickTab={(id) => setCategory(id)}
                choice={category}
                tabsContainerClass={classes.vendorTabs}
              />
              <Paper className={classes.searchBox}>
                <img src={searchSvg} alt="search" />
                <input
                  type="text"
                  placeholder="Search by Invoice Number or Case ID"
                  className={classes.searchInput}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </Paper>
            </Box>
          ) : (
            <Box height="5rem" />
          )}
          <Box>
            {showPaymentDetails ? (
              <SebiInvoiceTable
                invoices={invoiceList || []}
                loading={loadingInvoices}
                onSaved={() => getInvoiceList(page)}
                canEdit={!isVendor}
              />
            ) : (
              <>
              <Box style={{ padding: "1rem", textAlign: "left" }}>
                <Grid container spacing={{ xs: 1, sm: 2 }}>
                  <Grid item sm={2} xs={2}>
                    <Typography className={classes.tableHead}>Name</Typography>
                  </Grid>
                  <Grid item sm={2} xs={2}>
                    <Typography className={classes.tableHead}>
                      Invoice date
                    </Typography>
                  </Grid>
                  <Grid item sm={2} xs={2}>
                    <Typography className={classes.tableHead}>
                      Date of payment initiation
                    </Typography>
                  </Grid>
                  <Grid item sm={2} xs={2}>
                    <Typography className={classes.tableHead}>Case ID</Typography>
                  </Grid>
                  <Grid item sm={2} xs={2}>
                    <Typography className={classes.tableHead}>
                      Invoice Number
                    </Typography>
                  </Grid>
                  <Grid item sm={1} xs={1}>
                    <Typography className={classes.tableHead}>
                      Payment status
                    </Typography>
                  </Grid>
                  <Grid item sm={1} xs={1}>
                    <Typography className={classes.tableHead}></Typography>
                  </Grid>
                </Grid>
              </Box>
              <Paper
                style={{
                  boxShadow: "none",
                  border: "1px solid #E5E5E5",
                  borderRadius: "0",
                }}
              >
                {loadingInvoices ? (
                  <Box height="4rem">
                    <CircularCenteredLoader />
                  </Box>
                ) : (
                  invoiceList.map((invoice) => {
                    const isBankTransfer =
                      invoice?.payment_mode === "bank_transfer";
                    return (
                      <Grid
                        container
                        key={invoice.id}
                        alignItems="center"
                        spacing={{ xs: 1, sm: 2 }}
                        className={classes.tableRow}
                      >
                        <Grid item xs={2} sm={2}>
                          <Typography className={classes.tableData}>
                            {invoice.full_name_of_user}
                          </Typography>
                        </Grid>
                        <Grid item xs={2} sm={2}>
                          <Typography className={classes.tableData}>
                            {/* date of payment link generation */}
                            {invoice.date_and_time
                              ? formatDate(invoice.date_and_time)
                              : null}
                          </Typography>
                        </Grid>
                        <Grid item xs={2} sm={2}>
                          <Typography className={classes.tableData}>
                            {/* date of payment initiation */}
                            {isBankTransfer
                              ? "—"
                              : invoice.date_of_payment_initiation
                              ? formatDate(invoice.date_of_payment_initiation)
                              : null}
                          </Typography>
                        </Grid>
                        <Grid item xs={2} sm={2}>
                          <Typography className={classes.tableData}>
                            {invoice.disputeId}
                          </Typography>
                        </Grid>
                        <Grid item xs={2} sm={2}>
                          <Typography className={classes.tableData}>
                            {invoice.invoice_number}
                          </Typography>
                        </Grid>
                        <Grid item xs={1} sm={1}>
                          <Typography className={classes.tableData}>
                            {isBankTransfer
                              ? "Bank transfer"
                              : invoice.payment_status}
                          </Typography>
                        </Grid>
                        <Grid item xs={1} sm={1}>
                          {invoice.invoice_id && (
                            <IconButton
                              style={{
                                padding: 0,
                              }}
                              onClick={() => downloadInvoice(invoice.invoice_id)}
                            >
                              <Typography className={classes.tableData}>Download</Typography>
                              <img src={DownloadSvg} alt="download invoice" style={{marginLeft: "8px"}}/>
                            </IconButton>
                          )}
                        </Grid>
                      </Grid>
                    );
                  })
                )}
              </Paper>
              </>
            )}
            <Box style={{ padding: "16px" }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </div>
  );
}
